import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ApiResponseEnvelope,
  PaginatedListResult,
  PaginatedQueryOptions,
} from '@doctor-scheduling/shared/models';
import { Gender } from '@doctor-scheduling/shared/constants';
import { API_BASE_URL } from './api-base-url.token';
import { buildListHttpParams } from './list-query.util';

/** Patient row returned by list and detail endpoints. */
export interface PatientListItem {
  id: string;
  clinicId: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string;
  dateOfBirth: string | null;
  gender: Gender | null;
  bloodGroup: string | null;
  address: string | null;
  city: string | null;
  postcode: string | null;
  country: string | null;
  emergencyContact: string | null;
  medicalNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Payload for creating a new patient. */
export interface CreatePatientPayload {
  firstName: string;
  lastName: string;
  email?: string;
  phone: string;
  dateOfBirth?: string;
  gender?: Gender;
  bloodGroup?: string;
  address: string;
  city: string;
  postcode: string;
  country: string;
  emergencyContact?: string;
  medicalNotes?: string;
}

/** Partial payload for updating an existing patient. */
export interface UpdatePatientPayload extends Partial<CreatePatientPayload> {}

/** HTTP client for patient CRUD within the current clinic. */
@Injectable({
  providedIn: 'root',
})
export class PatientApi {
  /** Angular HTTP client used for API requests. */
  private readonly http = inject(HttpClient);
  /** NestJS API origin from the web app environment. */
  private readonly apiBaseUrl = inject(API_BASE_URL);
  /** Base URL for patient routes under `/api/v1/patients`. */
  private readonly baseUrl = `${this.apiBaseUrl}/api/v1/patients`;

  /**
   * Lists patients with Eaze-style page/sort/filter query params.
   */
  findAll(
    opts?: PaginatedQueryOptions
  ): Observable<ApiResponseEnvelope<PaginatedListResult<PatientListItem>>> {
    return this.http.get<ApiResponseEnvelope<PaginatedListResult<PatientListItem>>>(
      this.baseUrl,
      { params: buildListHttpParams(opts) }
    );
  }

  findOne(id: string): Observable<ApiResponseEnvelope<PatientListItem>> {
    return this.http.get<ApiResponseEnvelope<PatientListItem>>(`${this.baseUrl}/${id}`);
  }

  create(data: CreatePatientPayload): Observable<ApiResponseEnvelope<PatientListItem>> {
    return this.http.post<ApiResponseEnvelope<PatientListItem>>(this.baseUrl, data);
  }

  update(id: string, data: UpdatePatientPayload): Observable<ApiResponseEnvelope<PatientListItem>> {
    return this.http.put<ApiResponseEnvelope<PatientListItem>>(`${this.baseUrl}/${id}`, data);
  }

  delete(id: string): Observable<ApiResponseEnvelope<{ success: boolean }>> {
    return this.http.delete<ApiResponseEnvelope<{ success: boolean }>>(`${this.baseUrl}/${id}`);
  }
}
